import { CONTACT_FALLBACK_EMAIL } from '../../lib/contact'

/**
 * The chrome every assessment tool shares — the words around the questions.
 *
 * Separate from each tool's own copy because all three will use these, and a
 * locale should translate "Question 3 / 12" once rather than three times.
 *
 * `{n}`, `{total}`, `{max}`, `{avg}` are substituted at render. Placeholders
 * rather than string concatenation in the component, so a translator can move
 * the number to wherever the sentence needs it — Turkish puts it in a different
 * place, and a component that builds the sentence forecloses that.
 */
export const toolsUi = {
  ui: {
    /** Below the start button. Sets the expectation for how long this takes. */
    introNote:
      'No preparation, no research. Answer as your leadership team would actually answer if nobody were watching. The value is in the honesty, not in the score.',
    start: 'Start',
    questionCount: '{n} questions · about four minutes',
    question: 'Question',
    back: 'Back',
    next: 'Next',
    seeVerdict: 'See the verdict',
    /**
     * Shown when somebody presses Next or See the verdict without having
     * answered the question on screen. The button used to be disabled instead,
     * which is silent: it looks identical to a button that is broken.
     */
    needAnswer: 'Please choose an option to continue.',
    needAnswerLast: 'Please choose an option to see your result.',
    level: 'Level',
    youAreHere: 'you are here',
    scoreLine: 'Score {total} of {max} · average {avg} of 3.0',
    provocation: 'The question to take into your next leadership meeting',
    moves: 'What to do first',
    restart: 'Start again',
    /**
     * Offered on the intro when half-finished answers are found and are more
     * than half an hour old — a refresh restores silently instead, because
     * announcing a restore nobody noticed losing is noise.
     *
     * "nothing was sent to us" is worth saying and is literally true: the
     * answers are in this browser's own storage and never reach a server. See
     * `tools/progress.ts` for why that is a boundary rather than a shortcut.
     */
    resumeNote:
      'You answered some of these earlier, on this device. Your answers are still here — nothing was sent to us.',
    resume: 'Continue where you left off',
    ctaHeading: 'Want this pressure-tested against your actual numbers?',
    ctaLabel: 'Book a conversation',
    /**
     * Shown when the save failed but the report is displayed anyway. Says what
     * happened without asking them to do anything about it — the lead is our
     * problem, the report is theirs.
     */
    notSaved:
      'We could not save this to send you a copy, so take a screenshot if you want to keep it. Your result below is correct either way.',
  },

  gate: {
    heading: 'Where should we send it?',
    sub: 'Your result is ready. We will email you a copy and, if it is useful, one page on what organisations at your level usually do next.',
    name: 'Your name',
    namePlaceholder: 'Ada Lovelace',
    email: 'Work email',
    emailPlaceholder: 'you@company.com',
    company: 'Company',
    companyPlaceholder: 'Company name',
    consent:
      'Send me my result and occasional related material from Digitopia. I can unsubscribe at any time.',
    submit: 'Show me the result',
    sending: 'Saving…',
    privacy:
      'We use this to send your result and to follow up once. We do not sell or share it.',
    /**
     * What is still missing, said plainly, when the button is pressed before the
     * form is complete. One line per unfinished field, so somebody who cannot
     * see why nothing happened is told exactly what to do next.
     */
    missing: {
      heading: 'Just one more thing before we can show your result:',
      name: 'Please enter your name.',
      email: 'Please enter a valid work email address so we can send your result.',
      company: 'Please enter your company name.',
      consent: 'Please tick the consent box so we may email you the result.',
      challenge: 'Please complete the security check above the button.',
    },
    /**
     * Turnstile reported an error, or its script never arrived (an ad blocker,
     * a strict privacy setting, an old browser — Fatih's PostHog read of
     * 2026-10-02). The Worker refuses a tool lead with no token, so say why and
     * give the one address that receives mail, instead of a refusal that names
     * a check the reader cannot see.
     */
    challengeFailed: `The security check did not load in your browser. Reload the page or turn off your ad blocker for this site — or email us at ${CONTACT_FALLBACK_EMAIL}.`,
  },
}
